/**
 * Notification "ding" — synthesized on the fly with the Web Audio API so we
 * don't have to ship and cache an MP3.
 *
 * Why a single shared AudioContext:
 *   Chrome / Edge limit the number of concurrent AudioContexts per tab.
 *   Creating one per ding leaks them and starts throwing after ~10 events.
 *
 * Autoplay policy:
 *   Browsers refuse to play audio until the user has interacted with the
 *   page at least once. The first call after a fresh load may silently fail
 *   — that's fine, by the time a new ticket arrives the operator has almost
 *   certainly clicked something.
 */
let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const W = window as any;
  const AC = W.AudioContext || W.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    try {
      ctx = new AC();
    } catch {
      return null;
    }
  }
  const c = ctx as AudioContext;
  // Autoplay-policy: a suspended context (no user gesture yet) will silently
  // discard `start()` calls. Resuming is a no-op once allowed.
  if (c.state === 'suspended') {
    c.resume().catch(() => {});
  }
  return c;
}

/**
 * Two-note chime — short and pleasant, not the iOS "ping" everyone has
 * decision fatigue with. Roughly: E5 → A5 over ~300ms with a fast decay.
 */
export function playNotificationDing(): void {
  const c = getCtx();
  if (!c) return;

  const now = c.currentTime;
  const master = c.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
  master.connect(c.destination);

  // First note (E5 = 659.25 Hz)
  const o1 = c.createOscillator();
  o1.type = 'sine';
  o1.frequency.setValueAtTime(659.25, now);
  o1.connect(master);
  o1.start(now);
  o1.stop(now + 0.22);

  // Second note (A5 = 880 Hz), starts as the first one fades
  const o2 = c.createOscillator();
  o2.type = 'sine';
  o2.frequency.setValueAtTime(880, now + 0.12);
  o2.connect(master);
  o2.start(now + 0.12);
  o2.stop(now + 0.45);
}

// ── Mute preference (persisted) ──────────────────────────────────────────
const STORAGE_KEY = 'ggdesk:notifSoundMuted';

export function isNotificationSoundMuted(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) === '1';
}

export function setNotificationSoundMuted(muted: boolean): void {
  if (typeof localStorage === 'undefined') return;
  if (muted) localStorage.setItem(STORAGE_KEY, '1');
  else localStorage.removeItem(STORAGE_KEY);
}
